import { Amplify, Auth } from 'aws-amplify'
import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react'

Amplify.configure({
    Auth: {
        region: process.env.REACT_APP_AUTH_REGION,
        userPoolId: process.env.REACT_APP_AUTH_USER_POOL_ID,
        userPoolWebClientId: process.env.REACT_APP_AUTH_USER_POOL_WEB_CLIENT_ID,
        cookieStorage: {
            domain: process.env.REACT_APP_AUTH_COOKIE_STORAGE_DOMAIN,
            path: "/",
            expires: 365,
            sameSite: "strict",
            secure: true,
        },
        authenticationFlowType: "USER_PASSWORD_AUTH",
    },
})

export default (): UseAuth => useContext(authContext)

type ClientMetaData = {
    [key: string]: string
} | undefined

interface UseAuth {
    isLoaded: boolean
    isAuthenticated: boolean
    username: string
    signUp: (username: string, password: string, clientMetadata?: ClientMetaData) => Promise<Result>
    signIn: (username: string, password: string, clientMetadata?: ClientMetaData) => Promise<Result>
    confirmSignUp: (username: string, verificationCode: string, clientMetadata?: ClientMetaData) => Promise<Result>
    resendSignUp: (username: string, clientMetadata?: ClientMetaData) => Promise<Result>
    signOut: () => Promise<Result>
    forgotPassword: (username: string, clientMetadata?: ClientMetaData) => Promise<Result>
    resetPassword: (username: string, verificationCode: string, newPassword: string, clientMetadata?: ClientMetaData) => Promise<Result>
}

interface Result {
    success: boolean
    message: string
}

const authContext = createContext<UseAuth>({} as UseAuth)

export const ProvideAuth: FC<{ children: ReactNode }> = props => {
    const auth = useProvideAuth()
    return <authContext.Provider value={auth}> {props.children} </authContext.Provider>
}


const useProvideAuth = (): UseAuth => {
    const [isLoaded, setIsLoaded] = useState(false)
    const [isAuthenticated, setIsAuthenticated] = useState(false)
    const [username, setUsername] = useState("")

    const signUp = async (username: string, password: string, clientMetadata?: ClientMetaData): Promise<Result> => {
        try {
            await Auth.signUp({ username, password, clientMetadata })
            return {
                success: true,
                message: "Success",
            }
        } catch (error: any) {
            return {
                success: false,
                message: error.message
            }
        }
    }

    const signIn = async (username: string, password: string, clientMetadata?: ClientMetaData): Promise<Result> => {
        try {
            const result = await Auth.signIn(username, password, clientMetadata)
            setUsername(result.username)
            setIsAuthenticated(true)
            return {
                success: true,
                message: "Success",
            }
        } catch (error: any) {
            return {
                success: false,
                message: error.message
            }
        }
    }


    const confirmSignUp = async (username: string, verificationCode: string, clientMetadata?: ClientMetaData): Promise<Result> => {
        try {
            await Auth.confirmSignUp(username, verificationCode, {
                clientMetadata,
            })
            return {
                success: true,
                message: "Success",
            }
        } catch (error: any) {
            return {
                success: false,
                message: error.message
            }
        }
    }

    const resendSignUp = async (username: string, clientMetadata?: ClientMetaData): Promise<Result> => {
        try {
            await Auth.resendSignUp(username, clientMetadata)
            return {
                success: true,
                message: "Success",
            }
        } catch (error: any) {
            return {
                success: false,
                message: error.message
            }
        }
    }

    const signOut = async (): Promise<Result> => {
        try {
            await Auth.signOut()
            setIsAuthenticated(false)
            setUsername("")
            return {
                success: true,
                message: "Success",
            }
        } catch (error: any) {
            return {
                success: false,
                message: error.message
            }
        }
    }

    const forgotPassword = async (username: string, clientMetadata?: ClientMetaData): Promise<Result> => {
        try {
            await Auth.forgotPassword(username, clientMetadata)
            return {
                success: true,
                message: "Success",
            }
        } catch (error: any) {
            return {
                success: false,
                message: error.message
            }
        }
    }

    const resetPassword = async (username: string, verificationCode: string, newPassword: string, clientMetadata?: ClientMetaData): Promise<Result> => {
        try {
            await Auth.forgotPasswordSubmit(username, verificationCode, newPassword, clientMetadata)
            return {
                success: true,
                message: "Success",
            }
        } catch (error: any) {
            return {
                success: false,
                message: error.message
            }
        }
    }

    useEffect(() => {
        (async () => {
            try {
                const result = await Auth.currentAuthenticatedUser()
                setIsAuthenticated(true)
                setUsername(result.username)
                setIsLoaded(false)
            } catch (error) {
                setIsAuthenticated(false)
                setUsername("")
                setIsLoaded(false)
            }
            setIsLoaded(true)
        })()
    }, [])

    return {
        isLoaded,
        isAuthenticated,
        username,
        signUp,
        signIn,
        confirmSignUp,
        resendSignUp,
        signOut,
        forgotPassword,
        resetPassword,
    }
}